// @flow

import { useTranslation } from "react-i18next";
import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { SchemaTree, SchemaTreeType } from "../../../types/SchemaTree";
import Tooltip from "@kea-mod/common/tooltip";
import { KeyValueEntry } from "../../../types/KeyValueEntry";
import { ApiContext } from "../../../context/KeaAPI/newApi";

type Props = { parentSchemaId: number; closeAction: () => void; visible: boolean };

/**
 * This component is used to add a grading service to a schema.
 * It is shown inline in the tree view.
 */
export const TreeViewAddGradingServiceComponent = (props: Props) => {
  type State = {
    gradingServiceId: number | undefined;
    scenario: boolean;
    modelingLanguage: boolean;
    interactionLog: boolean;
    weight: string;
    keyValueEntries: KeyValueEntry[];
  };

  const { t } = useTranslation();
  const { schemaApi, schemaTreeApi, gradingServiceApi } = useContext(ApiContext);

  const parentSchema = schemaApi.elements.find((schema) => schema.id === props.parentSchemaId);
  if (!parentSchema)
    throw Error("Unknown schema with id " + props.parentSchemaId + " in TreeViewAddGradingServiceComponent.");

  const [gradingServiceEdgeCreated, setGradingServiceEdgeCreated] = useState(false);
  // custom dropdown state (replaces the native select for a searchable list with
  // per-service info, same pattern as the schema selection in the exercise form)
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [serviceSearch, setServiceSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const defaultState: State = {
    // can be used to reset.
    gradingServiceId: undefined,
    scenario: true,
    modelingLanguage: true,
    interactionLog: true,
    weight: "100",
    keyValueEntries: [],
  };
  const [state, setState] = useState<State>(defaultState);

  const reset = () => {
    setState(defaultState);
    setGradingServiceEdgeCreated(false);
  };

  // every time this component is shown, reset the form
  useEffect(() => {
    if (props.visible) reset();
  }, [props.visible]);

  // Close the dropdown when clicking outside of it.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // The list is long enough that scanning it is tedious - typing filters it down.
  useEffect(() => {
    if (dropdownOpen) {
      setServiceSearch("");
      searchInputRef.current?.focus();
    }
  }, [dropdownOpen]);

  const selectedService = gradingServiceApi.elements.find((gs) => gs.id === state.gradingServiceId);
  const filteredServices = gradingServiceApi.elements.filter((gs) =>
    gs.name.toLowerCase().includes(serviceSearch.trim().toLowerCase()),
  );

  const selectService = (id: number) => {
    setState({ ...state, gradingServiceId: id });
    setDropdownOpen(false);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, [event.target.name]: event.target.value });
  };

  const handleCheckboxChange = (event: any) => {
    setState({ ...state, [event.target.name]: event.target.checked });
  };

  /**
   * Creates a grading service edge in the database.
   */
  const createGradingService = async () => {
    const weightInt = parseInt(state.weight, 10);

    if (gradingServiceEdgeCreated) {
      alert(t("alreadyCreatingGradingServiceEdge"));
      return;
    } else if ([state.gradingServiceId, state.weight.trim()].some((i) => !i)) {
      alert(t("fillAllFields"));
      return;
    } else if (!(0 <= weightInt && weightInt <= 100)) {
      // negative or
      alert(t("weightBetween0And100"));
      return;
    }

    // create edge. id, createdAt, updatedAt are set by the server and discarded by the createSchemaTree function.
    let newEdge: SchemaTree = {
      id: -1,
      type: SchemaTreeType.GRADING_SERVICE,
      childId: state.gradingServiceId!!,
      parentId: props.parentSchemaId,
      weight: parseInt(state.weight, 10),
      scenario: state.scenario,
      language: state.modelingLanguage,
      interactionData: state.interactionLog,
      createdAt: "",
      updatedAt: "",
    };

    let edgeID = await schemaTreeApi.create(newEdge);
    console.log("Edge (schema tree) created with ID: " + edgeID);

    setGradingServiceEdgeCreated(true);
    // Not sure if it is possible to hit the button between this and the next line. But since both setXXX-functions
    // are async, it doesn't hurt track the state of the edge creation.
    reset();
    props.closeAction();
  };

  return (
    <div className="container mt-6" style={{ display: props.visible ? undefined : "none" }}>
      <div className="section">
        <div className="columns is-centered">
          <div className="column is-three-quarters">
            <div className="box">
              <h4 className="title is-4">
                {t("addchildgradingservice")}
                <Tooltip
                  icon="circle-info"
                  class="icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                  content={t("gradingServiceRoleTooltip")}
                ></Tooltip>
              </h4>

              {/* Grading service selection page. */}
              <div className="field">
                <label className="label" htmlFor="gradingServiceId">
                  {t("gradingservice")}
                </label>
                <div className={`dropdown ${dropdownOpen ? "is-active" : ""}`} style={{ width: "100%" }} ref={dropdownRef}>
                  <div className="dropdown-trigger" style={{ width: "100%" }}>
                    <button
                      type="button"
                      id="gradingServiceId"
                      className="input"
                      aria-haspopup="true"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        cursor: "pointer",
                        textAlign: "left",
                        appearance: "none",
                        WebkitAppearance: "none",
                      }}
                      onClick={() => setDropdownOpen((open) => !open)}
                    >
                      <span style={{ color: selectedService ? undefined : "#7a7a7a" }}>
                        {selectedService ? selectedService.name : t("selectGradingService")}
                      </span>
                      <span className="icon is-small" style={{ marginLeft: "0.5rem" }}>
                        &#9662;
                      </span>
                    </button>
                  </div>
                  <div className="dropdown-menu" role="menu" style={{ width: "100%" }}>
                    <div className="dropdown-content" style={{ maxHeight: "16rem", overflowY: "auto" }}>
                      <div className="dropdown-item" style={{ paddingTop: "0.25rem", paddingBottom: "0.5rem" }}>
                        <input
                          className="input is-small"
                          type="text"
                          placeholder={t("searchGradingService")}
                          value={serviceSearch}
                          ref={searchInputRef}
                          onChange={(event) => setServiceSearch(event.target.value)}
                        />
                      </div>
                      {filteredServices.map((gs) => (
                        <div
                          key={gs.id}
                          className="dropdown-item"
                          style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                        >
                          <span style={{ flex: 1, cursor: "pointer" }} onClick={() => selectService(gs.id)}>
                            {gs.name}
                          </span>
                          {gs.description ? (
                            <Tooltip
                              icon="circle-info"
                              class="icon tooltip no-border is-white has-tooltip-multiline has-tooltip-left"
                              content={gs.description}
                            ></Tooltip>
                          ) : null}
                        </div>
                      ))}
                      {filteredServices.length === 0 && (
                        <div className="dropdown-item has-text-grey">{t("noGradingServiceMatch")}</div>
                      )}
                    </div>
                  </div>
                </div>
                {selectedService && selectedService.description ? (
                  <p className="help">{selectedService.description}</p>
                ) : null}
              </div>

              {/*Boolean fields*/}
              <div className="field">
                <div className="label">
                  {t("scenario")}
                  <Tooltip
                    icon="circle-info"
                    class="icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                    content={t("scenarioGSTooltip")}
                  ></Tooltip>
                </div>
                <input name="scenario" type="checkbox" checked={state.scenario ?? ""} onChange={handleCheckboxChange} />
              </div>
              <div className="field">
                <div className="label">
                  {t("modelingLanguage")}
                  <Tooltip
                    icon="circle-info"
                    class="icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                    content={t("modelingLanguageGSTooltip")}
                  ></Tooltip>
                </div>
                <input
                  name="modelingLanguage"
                  type="checkbox"
                  checked={state.modelingLanguage ?? ""}
                  onChange={handleCheckboxChange}
                />
              </div>
              <div className="field">
                <div className="label">
                  {t("interactionLog")}
                  <Tooltip
                    icon="circle-info"
                    class="icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                    content={t("interactionLogGSTooltip")}
                  ></Tooltip>
                </div>
                <input
                  name="interactionLog"
                  type="checkbox"
                  checked={state.interactionLog ?? ""}
                  onChange={handleCheckboxChange}
                />
              </div>

              {/*Weight*/}
              <div className="field">
                <label className="label">
                  {t("weight")}
                  <Tooltip
                    icon="circle-info"
                    class="icon tooltip no-border is-white has-tooltip-multiline has-tooltip-top"
                    content={t("weightTooltipGradingService")}
                  ></Tooltip>
                </label>
                <input
                  className="input"
                  type="text"
                  name="weight"
                  value={state.weight ?? ""}
                  onChange={handleInputChange}
                />
              </div>

              {/*Buttons*/}
              <div className="columns mt-4">
                <button
                  className="button is-danger"
                  onClick={() => {
                    reset();
                    props.closeAction();
                  }}
                >
                  {t("cancel")}
                </button>
                <button className="button is-normal is-success" onClick={createGradingService}>
                  {t("create")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
